import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, Edit2, Trash2, Car, Gauge, Calendar, Fuel, 
  CreditCard, Users, Settings, Plus, UserMinus, Star, Eye, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useVehicles, useDrivers, useVehicleDrivers, VEHICLE_CATEGORIES, FUEL_TYPES, ACQUISITION_TYPES } from "@/hooks/useVehicles";
import type { Vehicle, VehicleInsert } from "@/hooks/useVehicles";
import { VehicleDialog } from "@/components/fleet/VehicleDialog";
import { VehicleChecklist } from "@/components/fleet/VehicleChecklist";
import { DriverCard } from "@/components/fleet/DriverCard";
import { DriverAssignmentDialog } from "@/components/fleet/DriverAssignmentDialog";
import { SEO } from "@/components/seo/SEO";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Actif", variant: "default" },
  maintenance: { label: "En maintenance", variant: "secondary" },
  sold: { label: "Vendu", variant: "outline" },
  scrapped: { label: "Réformé", variant: "destructive" },
};

const categoryColors: Record<string, string> = {
  vl: "bg-blue-500/10 text-blue-600",
  vu: "bg-orange-500/10 text-orange-600",
  pl: "bg-purple-500/10 text-purple-600",
  two_wheels: "bg-green-500/10 text-green-600",
  other: "bg-gray-500/10 text-gray-600",
};

export default function FleetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateVehicle, deleteVehicle } = useVehicles();
  const { drivers } = useDrivers();
  const { assignments, assignedDriverIds, isLoading: driversLoading, assignDriver, unassignDriver, setPrimaryDriver } = useVehicleDrivers(id);
  const { openDocument } = useDocumentPreview();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const { data: vehicle, isLoading, error } = useQuery({
    queryKey: ["vehicle", id],
    queryFn: async () => {
      if (!id || !user?.id) return null;

      const { data, error } = await supabase
        .from("vehicles")
        .select("*, gray_card_document:documents!vehicles_gray_card_document_id_fkey(*)")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();

      if (error) throw error;
      return data as Vehicle & { gray_card_document: Tables<"documents"> | null };
    },
    enabled: !!id && !!user?.id,
  });

  const handleUpdate = async (data: VehicleInsert) => {
    if (!id) return;
    await updateVehicle.mutateAsync({ id, ...data });
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteVehicle.mutateAsync(id);
    navigate("/fleet");
  };

  if (isLoading) {
    return (
      <div className="container max-w-5xl py-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="container max-w-5xl py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Car className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">Véhicule non trouvé</h3>
            <p className="text-muted-foreground mb-4">
              Ce véhicule n'existe pas ou vous n'y avez pas accès.
            </p>
            <Button onClick={() => navigate("/fleet")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à la flotte
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = statusConfig[vehicle.status] || statusConfig.active;
  const complianceColor = vehicle.compliance_score >= 80 
    ? "text-green-600" 
    : vehicle.compliance_score >= 50 
      ? "text-amber-600" 
      : "text-red-600";

  return (
    <>
      <SEO 
        title={`${vehicle.license_plate} - EasyFinder Fleet`}
        description={`Dossier du véhicule ${vehicle.brand} ${vehicle.model}`}
      />

      <div className="container max-w-5xl py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="space-y-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/fleet")}
              className="-ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Flotte
            </Button>

            <div className="flex items-center gap-3">
              {/* License plate badge */}
              <div className="bg-muted px-4 py-2 rounded-lg border-2 border-foreground/20 font-mono font-bold text-xl tracking-wider">
                {vehicle.license_plate}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-xs", categoryColors[vehicle.category])}>
                  {VEHICLE_CATEGORIES[vehicle.category]}
                </Badge>
                <Badge variant={statusInfo.variant} className="text-xs">
                  {statusInfo.label}
                </Badge>
              </div>
            </div>

            <h1 className="text-xl font-semibold">
              {vehicle.brand} {vehicle.model}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Modifier
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. Tous les documents liés seront détachés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - Tabs */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="documents">
              <TabsList>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="drivers">Conducteurs</TabsTrigger>
                <TabsTrigger value="mileage">Kilométrage</TabsTrigger>
              </TabsList>
              
              <TabsContent value="documents" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Dossier documentaire</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VehicleChecklist 
                      vehicleId={vehicle.id}
                      category={vehicle.category}
                      showAddButton
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="drivers" className="mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Conducteurs assignés</CardTitle>
                    <Button size="sm" onClick={() => setAssignDialogOpen(true)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Assigner
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {driversLoading ? (
                      <div className="py-8 text-center text-muted-foreground">Chargement...</div>
                    ) : assignments.length > 0 ? (
                      <div className="space-y-3">
                        {assignments.map((assignment) => (
                          <div key={assignment.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                {assignment.driver?.first_name?.charAt(0)}{assignment.driver?.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {assignment.driver?.first_name} {assignment.driver?.last_name}
                                  {assignment.is_primary && (
                                    <Badge variant="secondary" className="ml-2 text-xs">Principal</Badge>
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">{assignment.driver?.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!assignment.is_primary && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setPrimaryDriver.mutate(assignment.driver_id)}
                                >
                                  <Star className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => unassignDriver.mutate(assignment.driver_id)}
                              >
                                <UserMinus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground mb-3">Aucun conducteur assigné</p>
                        <Button variant="outline" size="sm" onClick={() => setAssignDialogOpen(true)}>
                          Assigner un conducteur
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <DriverAssignmentDialog
                  open={assignDialogOpen}
                  onOpenChange={setAssignDialogOpen}
                  drivers={drivers}
                  assignedDriverIds={assignedDriverIds}
                  onAssign={async (driverId) => { await assignDriver.mutateAsync({ driverId }); }}
                  onUnassign={async (driverId) => { await unassignDriver.mutateAsync(driverId); }}
                />
              </TabsContent>
              
              <TabsContent value="mileage" className="mt-4">
                <Card>
                  <CardContent className="py-12 text-center">
                    <Gauge className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">
                      L'historique du kilométrage sera disponible prochainement
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Compliance score */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className={cn("text-4xl font-bold mb-1", complianceColor)}>
                    {vehicle.compliance_score}%
                  </div>
                  <p className="text-sm text-muted-foreground">Score de conformité</p>
                </div>
                
                <Separator className="my-4" />

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-muted-foreground" />
                    <span>{new Intl.NumberFormat("fr-FR").format(vehicle.current_mileage)} km</span>
                  </div>
                  {vehicle.fuel_type && (
                    <div className="flex items-center gap-2">
                      <Fuel className="h-4 w-4 text-muted-foreground" />
                      <span>{FUEL_TYPES[vehicle.fuel_type]}</span>
                    </div>
                  )}
                  {vehicle.acquisition_type && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span>{ACQUISITION_TYPES[vehicle.acquisition_type]}</span>
                    </div>
                  )}
                  {vehicle.first_registration_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>
                        1ère immat. : {format(new Date(vehicle.first_registration_date), "MMMM yyyy", { locale: fr })}
                      </span>
                    </div>
                  )}
                  {vehicle.vin && (
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono text-xs truncate">{vehicle.vin}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Gray card */}
            {vehicle.gray_card_document && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Carte grise
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={() => openDocument(vehicle.gray_card_document!)}
                  >
                    <Eye className="h-4 w-4" />
                    Voir la carte grise
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {vehicle.notes && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {vehicle.notes}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Edit dialog */}
        <VehicleDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          vehicle={vehicle}
          onSubmit={handleUpdate}
        />
      </div>
    </>
  );
}
